import { BaseApiClient } from '@/services/baseApiClient';
import { RequestError, type RequestOptions, uploadFile } from '@/utils/request';
import { getQuery } from '@/components/filter/helpers';
import { PaginationField } from '@/components/pagination/constants';
import { captureException } from '@sentry/react';
import { ImportManualRecordFormField } from '@enmon/commons/lib/enmon';
import type { ViewManualRecords } from '@enmon/commons/lib/enmon/records/manual/view';
import type {
  CreateManualRecordArg,
  CreateManualRecordResult,
  DeleteManualRecordArg,
  DeleteManualRecordResult,
  GetManualRecordsArg,
  GetManualRecordsCountableResult,
  GetManualRecordsResult,
  UpdateManualRecordArg,
  UpdateManualRecordResult,
  ImportManualReadingsResult,
  ImportManualReadingsArgs,
  GetManualRecordResult,
  GetManualRecordArg,
} from './types';

export class ManualRecordsApiClient extends BaseApiClient {
  constructor() {
    super('api/records/manual');
  }

  public getById(manualRecordId: GetManualRecordArg, signal: RequestOptions['signal']): Promise<GetManualRecordResult> {
    return this.http.get(`${manualRecordId}`, { signal });
  }

  public get<TView extends ViewManualRecords = ViewManualRecords.Default>(
    filter: GetManualRecordsArg,
    view: TView,
    signal: RequestOptions['signal'],
  ): Promise<GetManualRecordsCountableResult<TView>> {
    return this.http.get('', { query: getQuery({ ...filter, view }), signal });
  }

  public getOne(filter: GetManualRecordsArg, signal: RequestOptions['signal']): Promise<GetManualRecordsResult> {
    return this.http.get('', { query: getQuery({ ...filter, [PaginationField.PageSize]: 1 }), signal });
  }

  public create(payload: CreateManualRecordArg): Promise<CreateManualRecordResult> {
    return this.http.post('', payload);
  }

  public delete(manualRecordId: DeleteManualRecordArg): Promise<DeleteManualRecordResult> {
    return this.http.delete(manualRecordId);
  }

  public update({ payload, manualRecordId }: UpdateManualRecordArg): Promise<UpdateManualRecordResult> {
    return this.http.patch(manualRecordId, payload);
  }

  public async import({ importFile, attachments }: ImportManualReadingsArgs): Promise<ImportManualReadingsResult> {
    const formData = new FormData();
    formData.append(ImportManualRecordFormField.ImportFile, importFile);

    attachments.forEach(attachment => formData.append(ImportManualRecordFormField.Attachments, attachment));

    try {
      return await uploadFile('api/manual-records/import', formData, { signal: undefined });
    } catch (e) {
      if (e instanceof RequestError && e.response.status === 412) {
        return await e.response.json<ImportManualReadingsResult>();
      }
      console.error(e);
      captureException(e);
      throw e;
    }
  }
}
